'use es6';

import { DSFieldRecord } from 'customer-data-filters-ui/public/core';
import PropertyOptionRecord from 'customer-data-objects/property/PropertyOptionRecord';
import I18n from 'I18n';
import { fromJS, List, Map as ImmutableMap, OrderedMap } from 'immutable';
import { ANALYTICS_VIEW, BLOGS, BLOG_AUTHOR, BLOG_TAG, SUBDOMAIN, SUBDOMAIN_ID, USER } from 'reference-resolvers/constants/ReferenceObjectTypes';
import { ANALYTICS_EVENT_COMPLETIONS, ANALYTICS_SALES_TEMPLATES, ANALYTICS_SOURCES, ANALYTICS_TOPICS, SOCIAL_CHANNELS, UNIFIED_FEEDBACK_TRENDS } from 'reporting-data/constants/dataTypes';
import * as unifiedDataTypes from 'reporting-data/constants/dataTypes/unified';
import * as overrides from 'reporting-data/lib/overrides';
import { createPropertiesFromUnifiedObjects, createUnifiedTranslationKey } from 'reporting-data/properties/data-type/unified';
import { dataTypeSupportsAnalyticsViews } from '../UnifiedAnalyticsViewFilterEditor/analyticsViewUtils';
const isDProperty = /^d\d$/;
const unifiedReferences = {
  filterId: ANALYTICS_VIEW,
  CONTENT_GROUP_ID: BLOGS,
  BLOG_AUTHOR_ID: BLOG_AUTHOR,
  TAG_IDS: BLOG_TAG,
  subdomain: SUBDOMAIN,
  DOMAIN_ID: SUBDOMAIN_ID,
  CREATED_BY: USER
};
export const unifiedPropertyResolvers = Object.keys(unifiedDataTypes).reduce((acc, key) => Object.assign({}, acc, {
  [key]: unifiedReferences
}), {});
const translatePropertyForDataType = dataType => (key, ...rest) => I18n.text(`reporting-data.properties.${createUnifiedTranslationKey(dataType)}.${key}`, ...rest);
export const getFilters = report => report.getIn(['config', 'filters', 'custom'], List());
export const getDrilldownFilters = report => getFilters(report).filter(filter => isDProperty.test(filter.get('property', '')));
const removeAnalyticsViewField = ({
  dataType,
  fields
}) => {
  return dataTypeSupportsAnalyticsViews(dataType) ? fields.delete('filterId') : fields;
};
export const modifyFieldsForDataType = ({
  dataType,
  config,
  fields,
  shouldIncludeAnalyticsViewField = false
}) => {
  const bulkDataTypeModifications = !shouldIncludeAnalyticsViewField ? [removeAnalyticsViewField] : [];
  const bulkModifiedFields = bulkDataTypeModifications.reduce((modifiedFields, modifyFunction) => modifyFunction({
    dataType,
    config,
    fields: modifiedFields
  }), fields);
  switch (dataType) {
    case ANALYTICS_SALES_TEMPLATES:
      if (getDrilldownFilters(ImmutableMap({
        config
      })).isEmpty()) {
        return OrderedMap();
      }
      return bulkModifiedFields;
    case ANALYTICS_TOPICS:
      return bulkModifiedFields.delete('source');
    case ANALYTICS_SOURCES:
      return bulkModifiedFields.delete('subdomain');
    case ANALYTICS_EVENT_COMPLETIONS:
      return bulkModifiedFields.delete('IS_SOFT_DELETED').delete('WAITLISTED');
    case unifiedDataTypes.UNIFIED_ADS_CAMPAIGNS:
      return bulkModifiedFields.delete('NAME').delete('NETWORK');
    case UNIFIED_FEEDBACK_TRENDS:
    case SOCIAL_CHANNELS:
      return OrderedMap();
    default:
      return bulkModifiedFields;
  }
};
const hideMissingEnums = field => {
  if (field.get('type') !== 'enumeration') return true;
  return unifiedReferences[field.get('name')] || field.get('options') && !field.get('options').isEmpty();
};
const hideRestrictedFields = scopes => field => {
  const fieldScope = {
    filterId: 'analytics-filters-read'
  }[field];
  return !fieldScope || scopes.includes(fieldScope);
};
export const isMetricFilteringSupported = (config, spec) => {
  return overrides.unifiedMetricFilters.enabled && spec.url.includes('activities/v2/reports');
};
export const getFields = ({
  dataType,
  spec,
  specialPropertyFilters,
  config,
  scopes = [],
  shouldIncludeAnalyticsViewField
}) => {
  const metricProperties = isMetricFilteringSupported(config, spec) ? spec.metrics : {};
  const fields = createPropertiesFromUnifiedObjects([spec.filters || {}, spec.metadata || {}, metricProperties], (key, ...rest) => translatePropertyForDataType(dataType)(key, ...rest));
  const initialFields = fields.reduce((acc, field) => acc.set(field.name, DSFieldRecord(fromJS(field)).update('options', options => options ? options.map(PropertyOptionRecord) : options).set('displayType', field.type).update('type', type => type === 'percent' || type === 'duration' ? 'number' : type).update('description', description => field.type === 'duration' ? I18n.text('reporting-ui-components.filterEditor.unified.durationDescription') : description)), specialPropertyFilters);
  return modifyFieldsForDataType({
    fields: initialFields,
    dataType,
    config,
    shouldIncludeAnalyticsViewField
  }).filter(hideMissingEnums).filter(hideRestrictedFields(scopes));
};